import { useRef, useState, type JSX } from 'react';
import { FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ACCEPTED_FILE_TYPES, type SheetData } from '@/lib/bulk-import/file';
import { StepPanel, plural } from './shared';

interface UploadStepProps {
  fileName: string | null;
  sheets: SheetData[];
  sheetIndex: number;
  /** Data rows in the picked sheet (header excluded). */
  dataRows: number;
  loading: boolean;
  error: string | null;
  onFile: (file: File) => void;
  onSheet: (index: number) => void;
  onNext: () => void;
}

export function UploadStep({
  fileName,
  sheets,
  sheetIndex,
  dataRows,
  loading,
  error,
  onFile,
  onSheet,
  onNext,
}: UploadStepProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function pick(files: FileList | null): void {
    const f = files?.[0];
    if (f) onFile(f);
  }

  return (
    <StepPanel
      title="Upload your list"
      description="A .csv or .xlsx file with one creator per row: a profile link or @handle, and optionally their niche, email and notes. Google Forms and Sheets exports work as they are."
    >
      <label
        htmlFor="bulk-file"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer.files);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors',
          dragging ? 'border-brand/60 bg-brand/[0.06]' : 'border-border hover:border-brand/40 hover:bg-accent/30',
        )}
      >
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        ) : (
          <Upload className="h-6 w-6 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold">{loading ? 'Reading the file…' : 'Drop a file here, or choose one'}</span>
        <span className="text-xs text-muted-foreground">
          .csv or .xlsx, up to 15 MB. Old .xls files aren&apos;t supported: save them as .xlsx first.
        </span>
        <input
          ref={inputRef}
          id="bulk-file"
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          className="sr-only"
          onChange={(e) => {
            pick(e.target.files);
            // Same file again should re-trigger a read.
            e.target.value = '';
          }}
        />
      </label>

      {error && (
        <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-sm text-destructive-strong">
          {error}
        </p>
      )}

      {fileName && sheets.length > 0 && !loading && (
        <div className="flex flex-wrap items-end justify-between gap-4 rounded-lg border border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-brand/25 bg-brand/10 text-brand">
              <FileSpreadsheet className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{fileName}</p>
              <p className="text-xs text-muted-foreground">
                {plural(dataRows, 'row')}
                {sheets.length > 1 ? ` in “${sheets[sheetIndex]?.name}”` : ''}
              </p>
            </div>
          </div>
          {sheets.length > 1 && (
            <div className="grid w-full gap-1.5 sm:w-64">
              <Label htmlFor="bulk-sheet">Sheet</Label>
              <Combobox
                id="bulk-sheet"
                value={String(sheetIndex)}
                onChange={(v) => v !== '' && onSheet(Number(v))}
                options={sheets.map((s, i) => ({
                  value: String(i),
                  label: s.name,
                  hint: plural(s.rows.length, 'row'),
                }))}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!fileName || loading || dataRows === 0}>
          Next: columns
        </Button>
      </div>
    </StepPanel>
  );
}
